import { DatePipe, KeyValue, CommonModule, KeyValuePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnInit, Output, inject, signal } from '@angular/core';
import { Backend } from 'src/app/backend-interface/backend';
import { UtcPipe } from 'src/app/pipes/format.pipe';
import { Policy, RequestPolicy } from 'src/app/service/global.service';
import { ToastService } from 'src/app/service/toast.service';
import { TranslationService } from 'src/app/service/translation.service';
import { TranslateModule } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { LoadingErrorComponent } from 'src/app/components/loading-error/loading-error.component';
import { ToastComponent } from 'src/app/components/toast/toast.component';
import { DetailsPolicyComponent } from '../details-policy/details-policy.component';
import Keycloak from 'keycloak-js';

@Component({
  selector: 'app-active-policies',
  templateUrl: './active-policies.component.html',
  styleUrls: ['./active-policies.component.scss'],
  standalone: true,
  imports: [
    CommonModule, 
    TranslateModule,
    KeyValuePipe,
    NgbTooltipModule,
    LoadingErrorComponent,
    ToastComponent,
    DetailsPolicyComponent,
  ],
  providers: [DatePipe, UtcPipe, ToastService, TranslationService]
})
export class ActivePoliciesComponent implements OnInit {

  lActivePolicies: Policy[] = [];
  mapPolicies = new Map<[string, string] | string, Policy[]>();
  currentpolicy?: Policy;
  predId: string = "";

  readonly loadingOerror = signal<boolean>(true);

  @Output() changeTab = new EventEmitter<string>();

  readonly datepipe = inject(DatePipe);
  readonly utcpipe = inject(UtcPipe);
  readonly toastService = inject(ToastService);
  readonly translationService = inject(TranslationService);
  readonly keycloak = inject(Keycloak);

  ngOnInit(): void {
    this.loadPolicies().then(() => {
      this.loading(0.5);
    });
  }

  private async loadPolicies() {
   
    this.lActivePolicies = await Backend.POLICIES.getPoliciesByFilters(
      ["SUBMITTED", "ACTIVATED", "DESACTIVATED", "REFUSED"], [], []
    );
    if (this.isAdmin()) {
      await this.setPoliciesByClient();
    } else {
      this.setPoliciesByOsApp();
    }
  }

  public isAdmin(): boolean {
    return this.keycloak.hasRealmRole('bo-dip-wl-admin');
  }

  today(): number {
    return Date.now();
  }

  timestampToDate(time: string) {
    const timestamp = this.utcpipe.transform(time);
    return Backend.FORMAT.timestampToDate(timestamp.getTime(), this.datepipe, this.translationService.getDatePipeLang()) ?? '';
  }

  horaire(timestamp: string) {
    const times = this.utcpipe.transform(timestamp);
    return Backend.FORMAT.horaire(times.getTime(), this.datepipe);
  }

  lOsApp() {
    return Backend.POLICIES.osApp(this.lActivePolicies);
  }

  private setPoliciesByOsApp() {
    for (const osapp of this.lOsApp()) {
      this.mapPolicies.set(osapp, Backend.POLICIES.getPolicyByAppOS(osapp[0], osapp[1], this.lActivePolicies));
    }
  }

  private async setPoliciesByClient() {
    const lPolWithClient = this.lActivePolicies.filter(p => p.clientPolicyDto);
    const lPolWithoutClient = this.lActivePolicies.filter(p => !p.clientPolicyDto);
    const lClients = await Backend.POLICIES.getClients();
    for (const client of lClients) {
      const lPClient = Backend.POLICIES.getPolicyByClient(client.name, lPolWithClient);
      if (lPClient.length > 0) {
        this.mapPolicies.set(client.name, lPClient);
      }
    }
    if (lPolWithoutClient.length > 0) {
      this.translationService.get('others').subscribe(other => {
        this.mapPolicies.set(other, lPolWithoutClient);
      });
    }
  }

  supprEsp(str: string) {
    return str.replaceAll(' ', '');
  }

  originalOrder = (
    a: KeyValue<[string, string] | string, Policy[]>,
    b: KeyValue<[string, string] | string, Policy[]>
  ): number => 0;

  getStateImage(policy: Policy) {
    if (policy.status === "ACTIVATED" && policy.activationDate) {
      return "assets/icons/Policies/power-active.svg";
    }
    if (policy.status === "SUBMITTED" && policy.submittedDate) {
      return "assets/icons/Policies/power-propose.svg";
    }
    if (policy.status === 'DESACTIVATED' && policy.desactivationDate) {
      return "assets/icons/Policies/power-desactive.svg";
    }
    if (policy.status === 'REFUSED' && policy.refusedDate) {
      return "assets/icons/Policies/cross.svg";
    }
    return "";
  }

  getState(policy: Policy) {
    if (policy.activationDate && !policy.desactivationDate) {
      return "Policy active";
    }
    if (!policy.activationDate && !policy.desactivationDate && policy.submittedDate) {
      return "In waiting of validation";
    }
    if (policy.desactivationDate) {
      return "Policy desactivate";
    }
    return "";
  }

  policySelectionnee(policy: Policy) {
    this.currentpolicy = policy;
    const del = document.getElementById("actifElem");
    if (del) {
      del.removeAttribute('id');
      del.setAttribute('id', this.predId);
    }
    const el = document.getElementById("elem" + this.supprEsp(policy.name.toLowerCase()) + policy.id);
    if (el) {
      el.id = "actifElem";
      this.predId = "elem" + this.supprEsp(policy.name.toLowerCase()) + policy.id;
    }
    document.getElementById('policyInfos')?.removeAttribute('hidden');
  }

  getOffset(el: HTMLElement) {
    const rect = el.getBoundingClientRect();
    return {
      left: rect.left + window.scrollX,
      top: rect.top + window.scrollY
    };
  }

  scrollTo() {
    const el = document.getElementById('policyInfos');
    if (el) {
      const offset = this.getOffset(el);
      window.scroll(offset.left, offset.top);
    }
  }

  duplicate(event: string) {
    this.changeTab.emit(event);
  }

  async reloadData() {
    this.mapPolicies = new Map();
    this.loadingOerror.set(true);
    await this.loadPolicies();
    await this.loading(1);
    this.hideAttestationDetails();
  }

  hideAttestationDetails() {
    const policyInfos = document.getElementById('policyInfos');
    if (policyInfos) {
      policyInfos.removeAttribute('hidden');
      policyInfos.setAttribute('hidden', 'hidden');
    }
  }

  async patchStatus(event: { action: string, policy: Policy }) {
    await Backend.POLICIES.patchStatusPolicy(event.policy.id, { status: event.action.toUpperCase() });
    await this.reloadData();
  }

  async loading(n: number) {
    await new Promise(r => setTimeout(r, n * 1000));
    this.loadingOerror.set(false);
  }

  async editPolicy(event: { policy: Policy, toast: string }) {
    if (event.policy.id) {
      const request: RequestPolicy = {
        id: event.policy.id,
        name: event.policy.name,
        idBW: event.policy.bwVersion!.id,
        activatedDetectors: event.policy.activatedDetectors
      };
      if (this.isAdmin()) {
        request.clientName = event.policy.clientPolicyDto?.name;
      }
      try {
        await Backend.POLICIES.modifyPolicy(request);
        this.toastService.show(event.toast, { classname: 'bg-success', delay: 5000 });
      } catch (error) {
        const err = error as HttpErrorResponse;
        this.toastService.show(err.error.error, { classname: 'bg-danger', delay: 5000 });
      }
    }
    await this.reloadData();
  }

  async deleteP(p: Policy) {
    try {
      await Backend.POLICIES.deletePolicy(p.id);
      const translations = await forkJoin({
        policy: this.translationService.get('security-policies-toast-policy'),
        delete: this.translationService.get('security-policies-toast-delete')
      }).toPromise();
      this.toastService.show(
        `${translations?.policy ?? ''}${p.name}${translations?.delete ?? ''}`,
        { classname: 'bg-success', delay: 5000 }
      );
    } catch (error) {
      const err = error as HttpErrorResponse;
      this.toastService.show(err.error.error, { classname: 'bg-danger', delay: 5000 });
    }
    await this.reloadData();
  }
}
